import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';


import { AngularFirestore } from '@angular/fire/compat/firestore';

import { getAuth } from 'firebase/auth';

import { SpinnerService } from '../../services/spinner.service';
import { ApiEntranceExitExhibitTransitDownloadFile, ApiSetEntranceExitExhibitTransitDownloadFilesRequest } from '../../api-client-module';
import { EntranceExitEvent } from '../../firestore-models/entrance-exit-event';
import { Floor } from '../../firestore-models/floor';
import { Exhibit } from '../../firestore-models/exhibit';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface DownloadDialogData {
  change_type: number;
  downloadFile?: ApiEntranceExitExhibitTransitDownloadFile;
  events: Array<EntranceExitEvent>;
  floors: Array<Floor>;
  exhibits: Array<Exhibit>;
}

@Component({
  selector: 'app-entrance-exit-exhibit-transit-download-dialog',
  templateUrl: './entrance-exit-exhibit-transit-download-dialog.component.html',
  styleUrls: ['./entrance-exit-exhibit-transit-download-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class EntranceExitExhibitTransitDownloadDialogComponent implements OnInit {

  //入退場管理展示来場状況ダウンロードファイル情報
  downloadFile?: ApiEntranceExitExhibitTransitDownloadFile;
  events!: Array<EntranceExitEvent>;
  floors!: Array<Floor>;
  allExhibits!: Array<Exhibit>;
  exhibits!: Array<Exhibit>;
  //プロジェクトID
  projectId: string;
  projectLanguages!: Array<ProjectLanguage>;

  isAdded: boolean = false;

  //フォーム
  downloadFormGroup!: UntypedFormGroup;
  entranceExitEventIdControl!: AbstractControl;
  exhibitIdControl!: AbstractControl;
  memoControl!: AbstractControl;
  startDateControl!: AbstractControl;
  endDateControl!: AbstractControl;

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  constructor(
    public dialogRef: MatDialogRef<EntranceExitExhibitTransitDownloadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DownloadDialogData,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.downloadFile = this.data.downloadFile;
    if (!this.downloadFile) {
      const authUser = getAuth().currentUser!;
      this.downloadFile = {
        entrance_exit_event_id: '',
        exhibit_id: '',
        uid: authUser.uid,
        memo: '',
        start_date: null as any,
        end_date: null as any,
        project_id: this.projectId,
        enabled: true
      };
      this.isAdded = true;
    }
    this.events = this.data.events;
    this.floors = this.data.floors;
    this.allExhibits = this.data.exhibits;
    this.exhibits = [];

    this.createForm();
    this.entranceExitEventIdControl = this.downloadFormGroup.get('entranceExitEventId') as AbstractControl;
    this.exhibitIdControl = this.downloadFormGroup.get('exhibitId') as AbstractControl;
    this.memoControl = this.downloadFormGroup.get('memo') as AbstractControl;
    this.startDateControl = this.downloadFormGroup.get('startDate') as AbstractControl;
    this.endDateControl = this.downloadFormGroup.get('endDate') as AbstractControl;
    this.changeEvent(this.entranceExitEventIdControl.value);

    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToEndDate() {
    return this.translate.instant('作成開始日より前の日は設定不可');
  }

  //フォームの値を各要素に代入
  private createForm() {
    const fb = new UntypedFormBuilder();
    this.downloadFormGroup = fb.nonNullable.group(
      {
        entranceExitEventId: new UntypedFormControl({value: this.downloadFile!.entrance_exit_event_id, disabled: !this.isAdded}, this.isAdded ? [Validators.required] : []),
        exhibitId: new UntypedFormControl({value: this.downloadFile!.exhibit_id, disabled: !this.isAdded}, this.isAdded ? [Validators.required] : []),
        memo: new UntypedFormControl({value: this.downloadFile!.memo, disabled: false}, []),
        startDate: new UntypedFormControl({value: this.downloadFile!.start_date, disabled: !this.isAdded}, []),
        endDate: new UntypedFormControl({value: this.downloadFile!.end_date, disabled: !this.isAdded}, []),
      }, {
        validators: [this.isValidDate]
      }
    );
  }

  //更新ボタン
  create(): void {
    if (this.downloadFormGroup.valid) {
      this.downloadFile!.entrance_exit_event_id = this.entranceExitEventIdControl.value;
      this.downloadFile!.exhibit_id = this.exhibitIdControl.value;
      this.downloadFile!.memo = this.memoControl.value;
      this.downloadFile!.start_date = this.startDateControl.value ? this.startDateControl.value : new Date('1970-01-01T00:00:00');
      this.downloadFile!.end_date = this.endDateControl.value ? this.endDateControl.value : new Date('2099-12-31T23:59:59');
      if (this.downloadFile) {
        let request: ApiSetEntranceExitExhibitTransitDownloadFilesRequest = {
          access_token: '',
          change_type: this.data.change_type,
          files: [this.downloadFile]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setEntranceExitExhibitTransitDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('作成を依頼しました。'));
              this.dialogRef.close();
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  changeEvent(value: any): void {
    let exhibits: Exhibit[] = [];
    for (const event of this.events) {
      if (value === event.id) {
        const floors = this.floors.filter(floor => event.floor_ids.includes(floor.id!));
        for (const exhibit of this.allExhibits) {
          for (const floor of floors) {
            if (!!floor.exhibit_ids && floor.exhibit_ids.includes(exhibit.id!)) {
              exhibits.push(exhibit);
              break
            }
          }
        }
        break;
      }
    }
    this.exhibits = exhibits;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // 作成期間整合制チェック
  isValidDate(ac: AbstractControl): ValidationErrors | null {
    const startDate = ac.get('startDate');
    const endDate = ac.get('endDate');
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
          endDate.setErrors({ before_start_date: true });
      } else {
        endDate.setErrors(null);
      }
    }
    return null;
  }

}
